import { useState } from "react";
import { CheckSquare, Link2, Lock, Paperclip, Plus, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTime, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const today = new Date().toISOString().slice(0, 10);
const inp = "text-xs border border-slate-300 rounded px-2 py-1 bg-white outline-none focus:border-sky-500";

export function Progress({ p, id }) {
  if (!p?.total) return <span className="text-[10px] text-slate-400" data-testid={`grc-progress-${id}`}>No plan</span>;
  return (
    <div className="w-24" data-testid={`grc-progress-${id}`}>
      <div className="flex justify-between text-[10px] num text-slate-600"><span>{p.done}/{p.total}</span><span>{p.pct}%</span></div>
      <div className="h-1.5 bg-slate-200 rounded mt-0.5 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${p.pct}%` }} /></div>
      {p.overdue > 0 && <div className="text-[10px] text-red-600 mt-0.5">{p.overdue} step(s) overdue</div>}
    </div>
  );
}

function Steps({ issue, call, ro }) {
  const [f, setF] = useState({ text: "", owner: "", target_date: "" });
  const add = async () => { if (await call("post", "steps", { ...f, target_date: f.target_date || null })) setF({ text: "", owner: "", target_date: "" }); };
  return (
    <div>
      <div className="eyebrow mb-2">Remediation steps</div>
      <div className="space-y-1.5" data-testid={`grc-steps-${issue.id}`}>
        {!issue.steps?.length && <div className="text-xs text-slate-500">No steps yet. Add the first one below.</div>}
        {(issue.steps || []).map((s) => {
          const late = !s.done && s.target_date && s.target_date < today;
          return (
            <div key={s.id} className="flex items-start gap-2 text-xs group" data-testid={`grc-step-${issue.id}-${s.id}`}>
              <button data-testid={`grc-step-toggle-${issue.id}-${s.id}`} disabled={ro} onClick={() => call("put", `steps/${s.id}`, { done: !s.done })} className="mt-0.5 text-slate-500 hover:text-emerald-600">
                {s.done ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4" />}
              </button>
              <div className="flex-1"><div className={s.done ? "line-through text-slate-400" : "text-slate-800"}>{s.text}</div>
                <div className="text-[10px] text-slate-500">{s.owner}{s.done && s.done_by ? ` · done by ${s.done_by}` : ""}</div></div>
              <span className={`num text-[10px] whitespace-nowrap ${late ? "text-red-600 font-medium" : "text-slate-500"}`}>{s.target_date || "-"}{late && " · OVERDUE"}</span>
              {!ro && <button data-testid={`grc-step-delete-${issue.id}-${s.id}`} onClick={() => call("delete", `steps/${s.id}`)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          );
        })}
      </div>
      {!ro && <div className="flex flex-wrap gap-1.5 mt-3">
        <input data-testid={`grc-step-text-${issue.id}`} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder="New step…" className={`${inp} flex-1 min-w-[160px]`} />
        <input data-testid={`grc-step-owner-${issue.id}`} value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} placeholder={issue.owner} className={`${inp} w-32`} />
        <input data-testid={`grc-step-date-${issue.id}`} type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} className={inp} />
        <button data-testid={`grc-step-add-${issue.id}`} disabled={!f.text.trim()} onClick={add} className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-sky-600 text-white disabled:opacity-40"><Plus className="w-3 h-3" />Add</button>
      </div>}
    </div>
  );
}

function Evidence({ issue, call, ro }) {
  const [f, setF] = useState({ text: "", url: "" });
  const add = async () => { if (await call("post", "evidence", f)) setF({ text: "", url: "" }); };
  return (
    <div>
      <div className="eyebrow mb-2">Evidence</div>
      <div className="space-y-1.5" data-testid={`grc-evidence-${issue.id}`}>
        {!issue.evidence?.length && <div className="text-xs text-slate-500">No evidence attached.</div>}
        {(issue.evidence || []).map((e) => (
          <div key={e.id} className="text-xs flex gap-2"><Paperclip className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
            <div><div className="text-slate-800">{e.text}{e.url && <a href={e.url} target="_blank" rel="noreferrer" className="ml-1.5 text-sky-700 underline inline-flex items-center gap-0.5"><Link2 className="w-3 h-3" />link</a>}</div>
              <div className="text-[10px] text-slate-500">{e.by} · {fmtTime(e.at)}</div></div></div>
        ))}
      </div>
      {!ro && <div className="flex flex-col gap-1.5 mt-3">
        <input data-testid={`grc-evidence-text-${issue.id}`} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder="Evidence note…" className={inp} />
        <div className="flex gap-1.5">
          <input data-testid={`grc-evidence-url-${issue.id}`} value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https:// link (optional)" className={`${inp} flex-1`} />
          <button data-testid={`grc-evidence-add-${issue.id}`} disabled={!f.text.trim()} onClick={add} className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-sky-500/50 text-sky-700 disabled:opacity-40"><Paperclip className="w-3 h-3" />Attach</button>
        </div>
      </div>}
    </div>
  );
}

export function RemediationPlan({ issue, reload }) {
  const { persona, personaLabel } = useApp();
  const ro = !persona?.permissions.includes("grc.manage");
  const call = async (method, path, body) => {
    try { await api[method](`/grc/issues/${issue.id}/${path}`, body); await reload(); return true; }
    catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e) || "Update failed"); else reload(); return false; }
  };
  return (
    <div className="bg-slate-50 border-t border-slate-200 px-4 py-4 grid lg:grid-cols-[1fr_340px] gap-6" data-testid={`grc-plan-${issue.id}`}>
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-xs">
          <span className="eyebrow">Overall target date</span>
          <input data-testid={`grc-plan-target-${issue.id}`} disabled={ro} type="date" defaultValue={issue.due_date || ""} key={issue.due_date} onBlur={(e) => e.target.value !== (issue.due_date || "") && call("put", "plan", { target_date: e.target.value || null })} className={inp} />
          <Progress p={issue.progress} id={`plan-${issue.id}`} />
        </div>
        {issue.investigation_notes && (
          <div className="panel p-3" data-testid={`grc-investigation-notes-${issue.id}`}>
            <div className="eyebrow mb-1">Investigation notes · raised by {issue.raised_by} from {issue.linked_alert} (wire {issue.linked_wire})</div>
            <div className="text-xs text-slate-700 whitespace-pre-wrap">{issue.investigation_notes}</div>
          </div>
        )}
        {ro && <div className="text-[11px] text-slate-500 flex items-center gap-1.5" data-testid={`grc-plan-readonly-${issue.id}`}><Lock className="w-3 h-3" />Read-only for {personaLabel}. Remediation plans require GRC management access.</div>}
        <Steps issue={issue} call={call} ro={ro} />
      </div>
      <Evidence issue={issue} call={call} ro={ro} />
    </div>
  );
}
